import { ErrorHandler, Injectable } from '@angular/core';
import { NavigationCancellationCode } from '@angular/router';

/**
 * Regex to detect chunk-load failures after deployment.
 * When a new build replaces old hashed JS files, the browser may still try to
 * load the old chunk URL (from the cached index.html). The server responds with
 * a 404 and Angular throws a ChunkLoadError. A hard refresh resolves this by
 * fetching the new index.html that references the new hash filenames.
 */
const CHUNK_LOAD_ERROR_REGEX = /Loading chunk.*failed|Failed to load module script/i;

/**
 * Minimum interval (ms) between automatic reloads to prevent infinite loop.
 * If a chunk load fails again within this window, we bail and let the user
 * manually refresh.
 */
const RELOAD_COOLDOWN_MS = 60_000;

@Injectable()
export class AppErrorHandler implements ErrorHandler {
  private lastReloadTime = 0;

  handleError(error: unknown): void {
    // NavigationCancelingError with cancellationCode === Redirect (0) is Angular's
    // internal mechanism for CanMatch / CanActivate guards that return a UrlTree.
    // The router DOES handle this correctly — it just redirects to the target URL.
    // With zone.js re-enabled, zone.js intercepts the error in the Observable chain
    // before Angular's router switchMap catchError runs, causing it to be logged as
    // an unhandled error at zone.js:1895. Suppressing it here is the correct fix.
    if (isRedirectCancellation(error)) return;

    // --- Chunk-load failure detection ---
    // After a deployment, old hashed JS/JS.map files no longer exist on the server.
    // The browser tries to load them from the cached index.html → 404 → cycle breaks.
    // A hard reload fetches the new index.html and resolves the issue.
    if (isChunkLoadError(error)) {
      const now = Date.now();
      if (now - this.lastReloadTime > RELOAD_COOLDOWN_MS) {
        this.lastReloadTime = now;
        console.warn('[AppErrorHandler] Detected chunk-load failure — likely a deployment.');
        console.warn('[AppErrorHandler] Performing auto-reload to fetch fresh app bundles.');
        if (typeof window !== 'undefined') {
          window.location.reload();
        }
        return;
      }
      // Cooldown not expired — fall through to console.error so the user sees it
    }

    console.error(error);
  }
}

function isRedirectCancellation(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  // Check structured property first (Angular 15+ NavigationCancelingError)
  const code = (error as any).cancellationCode ?? (error as any).type;
  if (code === NavigationCancellationCode.Redirect) return true;
  // Fallback: Angular formats the message as "NavigationCancelingError: Redirecting to <url>"
  return error.message.includes('Redirecting to');
}

function isChunkLoadError(error: unknown): boolean {
  if (error instanceof Error && CHUNK_LOAD_ERROR_REGEX.test(error.message)) {
    return true;
  }
  // Angular may also emit chunk errors via the error event's message
  if (typeof error === 'string' && CHUNK_LOAD_ERROR_REGEX.test(error)) {
    return true;
  }
  return false;
}
